import { isConditionGroup } from "./types.js";
import { applyOperator } from "./operators.js";
import { resolveSource, resolveValue } from "./resolvers.js";
/**
 * Evaluate a condition (single or group) against a context.
 * Returns true if the condition is met.
 */
export function evaluateCondition(condition, context) {
    if (isConditionGroup(condition)) {
        return evaluateGroup(condition, context);
    }
    return evaluateSingle(condition, context);
}
/**
 * Gate predicate for an OPTIONAL condition (e.g. `RuleAttachment.condition`,
 * the Automations builder's authored branch-IF): absent ⇒ unconditional
 * (always allowed); present ⇒ delegates to `evaluateCondition`. Single home
 * for the "does this optional condition allow the thing it guards to fire"
 * check, used by every field-scope check gate (validate bindings, server-data
 * rules, webhook checks) and the route/action family, on both server and
 * client, so the semantics can't drift between call sites.
 */
export function conditionAllows(condition, context) {
    return !condition || evaluateCondition(condition, context);
}
/**
 * Evaluate a condition with detailed results for each sub-condition.
 * Used by the testing sandbox to show step-by-step evaluation.
 */
export function evaluateConditionDetailed(condition, context) {
    if (isConditionGroup(condition)) {
        return evaluateGroupDetailed(condition, context);
    }
    return evaluateSingleDetailed(condition, context);
}
// --- Internal ---
function evaluateGroup(group, context) {
    if (group.conditions.length === 0) {
        // Vacuous truth for AND, false for OR
        return group.logic === "and";
    }
    if (group.logic === "and") {
        return group.conditions.every((c) => evaluateCondition(c, context));
    }
    // OR
    return group.conditions.some((c) => evaluateCondition(c, context));
}
function evaluateSingle(condition, context) {
    const left = resolveSource(condition.source, context);
    const right = resolveValue(condition.value, context);
    return applyOperator(condition.operator, left, right);
}
function evaluateGroupDetailed(group, context) {
    const children = group.conditions.map((c) => evaluateConditionDetailed(c, context));
    let passed;
    if (group.conditions.length === 0) {
        passed = group.logic === "and";
    }
    else if (group.logic === "and") {
        passed = children.every((c) => c.passed);
    }
    else {
        passed = children.some((c) => c.passed);
    }
    return {
        passed,
        logic: group.logic,
        children,
    };
}
function evaluateSingleDetailed(condition, context) {
    const left = resolveSource(condition.source, context);
    const right = resolveValue(condition.value, context);
    const passed = applyOperator(condition.operator, left, right);
    return {
        passed,
        resolvedLeft: left,
        resolvedRight: right,
        operator: condition.operator,
    };
}
//# sourceMappingURL=evaluator.js.map